import React, { useMemo } from 'react';

import { addSeconds } from 'date-fns';

import ActionButton from 'components/action-button';
import { DeliveryStatus, RbiOrderStatus, ServiceMode } from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';

import { MinimalAccountOrder } from './types';

interface IMinimalOrderActionProps {
  order: MinimalAccountOrder;
  loading: boolean;
  disabled: boolean;
  onTrack: () => void;
  onReorder: () => void;
  onFinish: () => void;
}

const notEnRouteStatus = new Set([
  DeliveryStatus.ORDER_CANCELLED,
  DeliveryStatus.ORDER_ABANDONED,
  DeliveryStatus.ORDER_DROPPED_OFF,
]);

export const MinimalOrderAction = ({
  order,
  loading,
  disabled,
  onTrack,
  onReorder,
  onFinish,
}: IMinimalOrderActionProps) => {
  const isEnRoute = useMemo(() => {
    if (!order.delivery) {
      return false;
    }

    const isDelivery = order.cart.serviceMode === ServiceMode.DELIVERY;
    return isDelivery && !!order.delivery.status && !notEnRouteStatus.has(order.delivery.status);
  }, [order.cart.serviceMode, order.delivery]);

  const isFinish = useMemo(() => {
    const isActive =
      !!order.fireOrderIn && addSeconds(new Date(order.createdAt), order.fireOrderIn) > new Date();
    return (
      order.cart.serviceMode === ServiceMode.TAKEOUT &&
      isActive &&
      order.status === RbiOrderStatus.INSERT_SUCCESSFUL
    );
  }, [order.cart.serviceMode, order.createdAt, order.fireOrderIn, order.status]);

  if (isEnRoute) {
    return (
      <ActionButton fullWidth testID="track-order-button" onPress={onTrack}>
        {t('trackOrder')}
      </ActionButton>
    );
  }

  if (isFinish) {
    return (
      <ActionButton fullWidth testID="finish-order-button" onPress={onFinish} disabled={disabled}>
        {t('finishOrder')}
      </ActionButton>
    );
  }

  return (
    <ActionButton
      fullWidth
      testID="reorder-button"
      onPress={onReorder}
      isLoading={loading}
      disabled={disabled}
      aria-label={
        order.orderNumberFormatted
          ? t('reorderWithOrderNumber', { ticketNumber: order.orderNumberFormatted })
          : t('reorder')
      }
    >
      {t('reorder')}
    </ActionButton>
  );
};
